import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {  map, catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStateService } from '../services/authState.service';
import { SessionExpiredService } from '../services/session-expired.service';

export const loggingInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authStateService = inject(AuthStateService);
  const sessionExpiredService = inject(SessionExpiredService);

  const publicEndpoints = [
    '/api/program/list/',
    '/api/program/:id/',
    'api/file/:name/'
  ];


  const isPublic = publicEndpoints.some(url => req.url.includes(url));

  let cloned = req;

  if (!isPublic) {
    // Check if request already has an Authorization header (e.g., from verifyOtp with tempToken)
    const hasExistingAuth = req.headers.has('Authorization');
    
    // Only add auth_token if no Authorization header exists
    if (!hasExistingAuth) {
      const token = sessionStorage.getItem('auth_token');

      if (token && token !== 'undefined' && token.trim() !== '') {
        cloned = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      }
    }
  }

  const handleSessionExpired = (shouldRedirect: boolean = true) => {
    // Clear tokens from sessionStorage
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('temp_token');
    // Clear userProgramsList when session expires
    sessionStorage.removeItem('userProgramsList');
    
    // Update auth state to logged out
    authStateService.updateAuthState(false);
    
    // Broadcast token expiry event to other tabs
    const expiryEvent = {
      type: 'TOKEN_EXPIRED',
      timestamp: Date.now()
    };
    localStorage.setItem('auth_token_expired', JSON.stringify(expiryEvent));
    // Remove the event after a short delay
    setTimeout(() => {
      localStorage.removeItem('auth_token_expired');
    }, 100);
    
    // Clear auth_token from localStorage to trigger storage event in other tabs
    const oldToken = localStorage.getItem('auth_token');
    if (oldToken) {
      localStorage.removeItem('auth_token');
    }
    
    // Show session expired modal
    sessionExpiredService.show();
    
    // Only redirect to home if explicitly requested (for actual session expiry)
    // Don't redirect on page refresh if user is on a different page
    if (shouldRedirect) {
      router.navigate(['/']);
    }
  };

  return next(cloned).pipe(
    map((response: any) => {
      // Check for statusCode 2004 in successful responses
      // Handle HttpResponse objects (when observe: 'response' is used)
      if (response instanceof HttpResponse) {
        const body = response.body;
        if (body && body.statusCode === 2004) {
          handleSessionExpired();
          throw new Error('Session expired');
        }
      }
      // Handle direct response objects (default behavior)
      else if (response && response.statusCode === 2004) {
        handleSessionExpired();
        throw new Error('Session expired');
      }
      return response;
    }),
    catchError((err) => {
      // Handle errors thrown from map operator (statusCode 2004 in successful responses)
      if (err instanceof Error && err.message === 'Session expired') {
        // Already handled in map operator, just rethrow
        return throwError(() => err);
      }
      
      if (err instanceof HttpErrorResponse) {
        // Handle statusCode 2004 in error responses
        if (err.error && err.error.statusCode === 2004) {
          handleSessionExpired();
          return throwError(() => err);
        }
        
        // Handle token expiry (401 Unauthorized)
        // Only logout if we actually sent a token with the request (not for public endpoints or requests without auth)
        if (err.status === 401) {
          const currentToken = sessionStorage.getItem('auth_token');
          const requestHadAuth = cloned.headers.has('Authorization');
          
          // Only handle if there was a token AND we actually sent it with the request
          // This prevents logout on 401 errors for public endpoints or requests that didn't include auth
          if (currentToken && requestHadAuth) {
            // This is an actual session expiry - redirect to home
            handleSessionExpired(true);
          }
        }
      }
      return throwError(() => err);
    })
  );
};
